import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { Icon } from '@lib/ui';
import { cn } from '@lib/utils';
import { createScopedKeydownHandler } from '@lib/utils/dom/createScopedKeydownHandler';
import { useAccordionContext } from './Accordion.context';
import { useAccordionItemContext } from './AccordionItem.context';

type AccordionButtonProps = {
  children?: ReactNode | ((params: { open: boolean }) => ReactNode);
  className?: string | ((params: { open: boolean }) => string);
  /** Custom icon element, or false/null to hide the default chevron */
  icon?: ReactNode | ((params: { open: boolean }) => ReactNode) | false | null;
  /** Additional classes for the icon wrapper */
  iconClassName?: string;
  disabled?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
} & Omit<ComponentPropsWithoutRef<'button'>, 'children' | 'className'>;

const AccordionButton = forwardRef<HTMLButtonElement, AccordionButtonProps>(
  ({ children, className, icon, iconClassName, disabled, onClick, onKeyDown, ...rest }, ref) => {
    const ctx = useAccordionContext();
    const { value } = useAccordionItemContext();
    const isActive = ctx.isItemActive(value);

    // Determine the icon to render
    const renderIcon = () => {
      // Explicitly disabled
      if (icon === false || icon === null) return null;

      // Custom icon (ReactNode or render function)
      if (icon !== undefined) {
        return typeof icon === 'function' ? icon({ open: isActive }) : icon;
      }

      // Default chevron
      return (
        <Icon
          name="chevron-down"
          className={cn(
            'transition-transform duration-200',
            isActive && 'rotate-180',
            iconClassName
          )}
        />
      );
    };

    return (
      <button
        {...rest}
        ref={ref}
        data-accordion-control
        disabled={disabled}
        className={cn(
          'cursor-pointer',
          typeof className === 'function' ? className({ open: isActive }) : className
        )}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          onClick?.(event);
          ctx.onChange(value);
        }}
        type="button"
        aria-expanded={isActive}
        aria-controls={`${ctx.panelId}-${value}`}
        id={`${ctx.buttonId}-${value}`}
        onKeyDown={createScopedKeydownHandler({
          siblingSelector: '[data-accordion-control]',
          parentSelector: '[data-accordion]',
          activateOnFocus: false,
          loop: ctx.loop,
          orientation: 'vertical',
          onKeyDown,
        })}
      >
        {typeof children === 'function' ? children({ open: isActive }) : children}
        {renderIcon()}
      </button>
    );
  }
);

AccordionButton.displayName = 'AccordionButton';

export { AccordionButton };
export type { AccordionButtonProps };
